import React, { useEffect, useState } from "react";
import { db } from "../../api";
import { collection, onSnapshot } from "../../api";

export default function ReportsOverview() {
  const [stats, setStats] = useState({
    totalReservations: 0,
    activeRentals: 0,
    totalRevenue: 0,
    pendingReservations: 0,
    completedReservations: 0,
    cancelledReservations: 0,
    totalInventoryItems: 0,
    availableItems: 0
  });
  
  const [recentActivity, setRecentActivity] = useState([]);

  const [categoryStats, setCategoryStats] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);

  useEffect(() => {
    // Fetch reservations stats
    const unsubReservations = onSnapshot(collection(db, "reservations"), (snapshot) => {
      const reservations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const totalReservations = reservations.length;
      const activeRentals = reservations.filter(r => r.status === 'active').length;
      const pendingReservations = reservations.filter(r => r.status === 'pending').length;
      const completedReservations = reservations.filter(r => r.status === 'completed').length;
      const cancelledReservations = reservations.filter(r => r.status === 'cancelled').length;

      const totalRevenue = reservations
        .filter(r => r.status === 'completed')
        .reduce((sum, r) => sum + (parseFloat(r.totalCost) || 0), 0);

      // Category statistics
      const categoryMap = {};
      reservations.forEach(r => {
        const cat = r.category || 'Uncategorized';
        if (!categoryMap[cat]) {
          categoryMap[cat] = { count: 0, revenue: 0 };
        }
        categoryMap[cat].count++;
        if (r.status === 'completed') {
          categoryMap[cat].revenue += parseFloat(r.totalCost) || 0;
        }
      });

      const categoryStatsArray = Object.entries(categoryMap)
        .map(([category, data]) => ({
          category,
          count: data.count,
          revenue: data.revenue.toFixed(2)
        }))
        .sort((a, b) => b.count - a.count);

      setCategoryStats(categoryStatsArray);

      // Monthly revenue (last 6 months)
      const monthlyMap = {};
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthlyMap[key] = 0;
      }

      reservations
        .filter(r => r.status === 'completed' && r.createdAt)
        .forEach(r => {
          const date = r.createdAt.toDate ? r.createdAt.toDate() : new Date(r.createdAt);
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (monthlyMap[key] !== undefined) {
            monthlyMap[key] += parseFloat(r.totalCost) || 0;
          }
        });

      const monthlyRevenueArray = Object.entries(monthlyMap).map(([month, revenue]) => ({
        month,
        revenue: revenue.toFixed(2)
      }));

      setMonthlyRevenue(monthlyRevenueArray);

      // Get recent activity (last 10 reservations)
      const sortedReservations = reservations
        .sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return bTime - aTime;
        })
        .slice(0, 10);

      setRecentActivity(sortedReservations);

      setStats(prev => ({
        ...prev,
        totalReservations,
        activeRentals,
        totalRevenue,
        pendingReservations,
        completedReservations,
        cancelledReservations
      }));
    });

    // Fetch inventory stats
    const unsubInventory = onSnapshot(collection(db, "inventory"), (snapshot) => {
      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const totalInventoryItems = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const availableItems = items.reduce((sum, item) => sum + (item.available || item.quantity || 0), 0);

      setStats(prev => ({
        ...prev,
        totalInventoryItems,
        availableItems
      }));
    });

    return () => {
      unsubReservations();
      unsubInventory();
    };
  }, []);

  const StatCard = ({ title, value, subtitle, color = "burgundy" }) => (
    <div className="bg-white border border-gray-300 rounded shadow p-6">
      <h3 className={`text-${color} text-lg font-medium mb-2`}>{title}</h3>
      <p className="text-3xl font-bold text-navy mb-1">{value}</p>
      {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
    </div>
  );

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Reservations" 
          value={stats.totalReservations}
          subtitle="All time"
        />
        <StatCard 
          title="Active Rentals" 
          value={stats.activeRentals}
          subtitle="Currently rented out"
          color="blue-600"
        />
        <StatCard 
          title="Completed hire value" 
          value={`$${stats.totalRevenue.toFixed(2)}`}
          subtitle="From completed rentals"
          color="green-600"
        />
        <StatCard 
          title="Available Items" 
          value={`${stats.availableItems}/${stats.totalInventoryItems}`}
          subtitle="Available/Total inventory"
        />
      </div>

      {/* Status Breakdown */}
      <div className="bg-white border border-gray-300 rounded shadow p-6">
        <h3 className="text-burgundy text-lg font-medium mb-4">Reservation Status Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.pendingReservations}</p>
            <p className="text-sm text-gray-600">Pending</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.activeRentals}</p>
            <p className="text-sm text-gray-600">Active</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{stats.completedReservations}</p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{stats.cancelledReservations}</p>
            <p className="text-sm text-gray-600">Cancelled</p>
          </div>
        </div>
      </div>

      {/* Category Performance */}
      <div className="bg-white border border-gray-300 rounded shadow p-6">
        <h3 className="text-burgundy text-lg font-medium mb-4">Category Performance</h3>
        {categoryStats.length === 0 ? (
          <p className="text-gray-600">No category data available</p>
        ) : (
          <div className="space-y-3">
            {categoryStats.map((cat, index) => (
              <div key={index} className="border-b border-gray-200 last:border-b-0 pb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-navy">{cat.category}</span>
                  <span className="text-sm text-gray-600">{cat.count} rentals</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-4">
                    <div
                      className="bg-burgundy h-2 rounded-full"
                      style={{ width: `${Math.min((cat.count / categoryStats[0].count) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-green-600">${cat.revenue}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Monthly Revenue Trend */}
      <div className="bg-white border border-gray-300 rounded shadow p-6">
        <h3 className="text-burgundy text-lg font-medium mb-4">Monthly completed hire value (Last 6 Months)</h3>
        {monthlyRevenue.length === 0 ? (
          <p className="text-gray-600">No completed hire value available</p>
        ) : (
          <div className="space-y-2">
            {monthlyRevenue.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 w-24">{item.month}</span>
                <div className="flex-1 mx-4">
                  <div className="bg-gray-200 rounded-full h-6 relative">
                    <div
                      className="bg-green-500 h-6 rounded-full flex items-center justify-end pr-2"
                      style={{
                        width: `${Math.min((parseFloat(item.revenue) / Math.max(...monthlyRevenue.map(m => parseFloat(m.revenue)))) * 100, 100)}%`,
                        minWidth: item.revenue > 0 ? '40px' : '0'
                      }}
                    >
                      <span className="text-xs text-white font-medium">${item.revenue}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-gray-300 rounded shadow p-6">
        <h3 className="text-burgundy text-lg font-medium mb-4">Recent Activity</h3>
        {recentActivity.length === 0 ? (
          <p className="text-gray-600">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                <div className="flex-1">
                  <p className="font-medium text-navy">
                    {activity.itemName || 'Unknown Item'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {activity.category} • {formatDate(activity.createdAt)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">
                    ${activity.totalCost || 0}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(activity.status || 'pending')}`}>
                    {(activity.status || 'pending').toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}